import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Chip, Autocomplete, CircularProgress,
    Container, Tabs, Tab
} from '@mui/material';
import { userService, authService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

// ─────────────────────────────────────────────────────────────────────────────
// UserProfile — fixes in this version:
//
// 1. RESET PASSWORD AS A TAB. The page now has two tabs — Profile and
//    Reset password — instead of password living on a separate page
//    (/auth/change-password remains routed for deep links, but the profile
//    page is now self-contained).
//
// 2. NO PROFILE PHOTO (by design). The identity system uses the first-name
//    initial in a terracotta circle everywhere — no photo upload, nothing to
//    moderate, uniform for every user.
//
// 3. SKILL CHIPS ARE INERT. Clicking a skill no longer navigates anywhere
//    (the rendered app was routing skill clicks to a dead path — chips here
//    explicitly stop propagation and carry no href/component). Interested
//    skills can only be removed via the chip's ×. (Earned skills are hidden:
//    they aren't tracked yet, N18 pt 1.1.)
//
// API wiring: getProfile, updateProfile (multipart), getAvailableSkills,
// getUserSkills, saveUserSkills — unchanged from the reconciled version.
// Password: authService.changePassword (api.js), POST /api/auth/change_password
// with oldPassword, newPassword and confirmPassword (N18 pt 3).
// ─────────────────────────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', mb: 1 }}>
        {children}
    </Typography>
);

// Stop clicks inside skill areas from reaching any ancestor click/row handler —
// this is what was sending skill clicks to a dead route.
const stopAll = (e) => { e.stopPropagation(); e.preventDefault?.(); };

const UserProfile = () => {
    const { showToast } = useToast();

    const [tab, setTab] = useState(0);

    // ── Profile tab state ──────────────────────────────────────────────────────
    const [loading, setLoading]     = useState(true);
    const [saving, setSaving]       = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName]   = useState('');
    const [email, setEmail]         = useState('');

    const [availableSkills, setAvailableSkills]   = useState([]); // [{id, name}]
    const [interestedSkills, setInterestedSkills] = useState([]); // [{id, name}] selected
    const [skillsLoaded, setSkillsLoaded]         = useState(false); // the saved skills came back (N18 pt 1)
    // Removed 2 Nov 2026 (N18 pt 1.1): earnedSkills, for the hidden Earned Skills section.

    // ── Reset-password tab state ───────────────────────────────────────────────
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword]         = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        let active = true;
        const load = async () => {
            try {
                const [profileRes, availRes, userSkillsRes] = await Promise.all([
                    userService.getProfile().catch(() => null),
                    userService.getAvailableSkills().catch(() => null),
                    userService.getUserSkills().catch(() => null),
                ]);
                if (!active) return;

                // Pre-fill: the profile payload shape varies by endpoint —
                // unwrap the common envelopes (data.user, data.data, data).
                const p = profileRes?.data?.userData
                    ?? profileRes?.data?.userData
                    ?? profileRes?.data
                    ?? {};
                setFirstName(p.first_name ?? p.firstName ?? p.fname ?? '');
                setLastName(p.last_name ?? p.lastName ?? p.lname ?? '');
                setEmail(p.email ?? p.email ?? '');

                const availList =
                    availRes?.data?.skills ??
                    availRes?.data?.data?.skills ??
                    availRes?.data?.data ??
                    availRes?.data ??
                    [];
                setAvailableSkills(
                    Array.isArray(availList)
                        ? availList.map(skill => ({
                            id: skill.id,
                            name: skill.skill_name
                        }))
                        : []
                );

                // The saved skills come back as data.skills, each { id, skill_name } (N18 pt 1), so the page shows what
                // Update saved. Removed 2 Nov 2026 (N18 pt 1): reading interested, interestedSkills or selected, which the
                // server never sends, so the saved skills never showed and Update looked as if it saved nothing.
                const us = userSkillsRes?.data?.data ?? userSkillsRes?.data ?? {};
                setInterestedSkills(Array.isArray(us.skills)
                    ? us.skills.map(skill => ({ id: skill.id, name: skill.skill_name }))
                    : []);
                setSkillsLoaded(Array.isArray(us.skills));
            } catch {
                showToast('Failed to load profile.', 'error');
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => { active = false; };
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('first_name', firstName);
            formData.append('last_name', lastName);
            // Removed 2 Nov 2026 (N18 pt 4): sending the email, which the profile never changes (the server ignores it,
            // S-26 pt 1.3).
            await userService.updateProfile(formData);

            // The interested skills by ID, all of them, so removing the last one saves none (N18 pt 1); only when they
            // loaded, so a failed load doesn't save an empty list over the saved ones.
            if (skillsLoaded) await userService.saveUserSkills(interestedSkills.map(s => s.id));

            showToast('Profile updated.', 'success');
        } catch {
            showToast('Failed to update profile.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const removeInterested = (id) =>
        setInterestedSkills(prev => prev.filter(s => s.id !== id));

    // The current password, the new one and its confirmation go to authService.changePassword (N18 pt 3).
    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword) {
            showToast('Enter your current and new password.', 'error');
            return;
        }
        if (newPassword.length < 8) {
            showToast('New password must be at least 8 characters.', 'error');
            return;
        }
        if (newPassword !== confirmPassword) {
            showToast('New passwords do not match.', 'error');
            return;
        }
        // Removed 2 Nov 2026 (N18 pt 3): the check for a missing authService.changePassword and its 'Password change is
        // not wired to the API yet' message; the method is there, and the change works end to end.
        setChangingPassword(true);
        try {
            await authService.changePassword(
                currentPassword,
                newPassword,
                confirmPassword,
            );
            showToast('Password updated.', 'success');
            setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to update password.';
            showToast(msg, 'error');
        } finally {
            setChangingPassword(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={24} sx={{ color: 'text.secondary' }} />
            </Box>
        );
    }

    return (
        <Container maxWidth="sm">
            <Box sx={{ maxWidth: 720, py: 3, px: 2 }}>
                <Typography sx={{ fontSize: '21px', fontWeight: 500, color: 'text.primary', mb: 2, letterSpacing: '-0.02em' }}>
                    Profile
                </Typography>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
                    <Tab label="Profile" />
                    <Tab label="Reset password" />
                </Tabs>

                {/* ── Tab 0: Profile ──────────────────────────────────────────── */}
                {tab === 0 && (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <SectionLabel required>First Name</SectionLabel>
                            <TextField fullWidth size="small" value={firstName} onChange={e => setFirstName(e.target.value)} />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <SectionLabel required>Last Name</SectionLabel>
                            <TextField fullWidth size="small" value={lastName} onChange={e => setLastName(e.target.value)} />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <SectionLabel required>Email</SectionLabel>
                            <TextField fullWidth size="small" value={email} onChange={e => setEmail(e.target.value)} disabled /> 
                            
                        </Box>

                        {/* Interested Skills picker. disablePortal keeps the
                            dropdown INSIDE this stopAll wrapper — portalled
                            options render at document root and escape the
                            wrapper's click guard, which is how a stray click
                            handler upstream was catching them. Chips are inert
                            (clickable=false); remove only via ×. */}
                        <Box sx={{ mb: 3 }} onClick={stopAll}>
                            <SectionLabel>Interested Skills</SectionLabel>
                            <Autocomplete
                                multiple
                                size="small"
                                disablePortal
                                options={availableSkills}
                                getOptionLabel={(o) => o.name ?? ''}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={interestedSkills}
                                onChange={(_, val) => setInterestedSkills(val)}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Select your interested skills…" />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return (
                                            <Chip
                                                key={option.id ?? key}
                                                variant="outlined"
                                                label={option.name}
                                                {...tagProps}
                                                clickable={false}
                                            />
                                        );
                                    })
                                }
                            />
                        </Box>

                        {/* Selected Skills */}
                        <Box sx={{ mb: 3 }} onClick={stopAll}>
                            <SectionLabel>Selected Skills ({interestedSkills.length})</SectionLabel>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {interestedSkills.length === 0 ? (
                                    <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>None selected yet.</Typography>
                                ) : interestedSkills.map(s => (
                                    <Chip
                                        key={s.id}
                                        variant="filled"
                                        label={s.name}
                                        onDelete={() => removeInterested(s.id)}
                                        onClick={stopAll}
                                        clickable={false}
                                    />
                                ))}
                            </Box>
                        </Box>

                        {/* Earned Skills is hidden: skills earned aren't tracked yet (N18 pt 1.1, agreed 2 Nov). Removed
                            2 Nov 2026 (N18 pt 1.1): the section, which read the earned skills from a list the server
                            never sent there, so it always read 'No earned skills yet.' */}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="contained" onClick={handleSave} disabled={saving}>
                                {saving ? 'Updating…' : 'Update Profile'}
                            </Button>
                        </Box>
                    </>
                )}

                {/* ── Tab 1: Reset password ───────────────────────────────────── */}
                {tab === 1 && (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <SectionLabel required>Current password</SectionLabel>
                            <TextField fullWidth size="small" type="password" autoComplete="current-password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <SectionLabel required>New password</SectionLabel>
                            <TextField fullWidth size="small" type="password" autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} helperText="At least 8 characters." />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <SectionLabel required>Confirm new password</SectionLabel>
                            <TextField fullWidth size="small" type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="contained" onClick={handleChangePassword} disabled={changingPassword}>
                                {changingPassword ? 'Updating…' : 'Update password'}
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Container>
    );
};

export default UserProfile;
